/* ---------------------------------------------------------------------------
   STL 2026 — Recently Viewed: fix up the cards Madrid injects

   Two things, both consequences of the same fact: `assets/recently-viewed.js`
   builds this section by fetching a search-results page and assigning
   `box.innerHTML`, long after the page has loaded. Anything that runs against
   product cards at load time therefore never sees these ones.

   1. ORDER — the cards arrive in Shopify's search order, not the order they
      were viewed. See below.
   2. IMAGE SLIDERS — the cards are real `.product-card-js` Swipers with 6-8
      slides each, but nothing initialises them, so they do not swipe the way
      the PLP's do. See `initCardSliders` at the foot of this file.

   ---------------------------------------------------------------------------
   1. ORDER: put the most recent first

   Serena, 3 Sep 2026. Madrid's Recently Viewed does not render its cards in the
   order you viewed them.

   WHY. `assets/recently-viewed.js` reads the stored history, builds a SEARCH
   query out of it — `q=id:A%20OR%20id:B%20OR%20…` — fetches the results page and
   injects the markup wholesale. Shopify returns search results in its own order,
   which has nothing to do with the order of the terms in the query, so the
   history order is thrown away at the fetch. It is not a reversal and cannot be
   fixed by reversing: the order is simply whatever search decided.

   Note this is easy to "verify" wrongly. On a freshly cleared history the two
   orders can agree by coincidence — newer products tend to have higher ids, and
   search returning by descending id then looks exactly like newest-first. A test
   only proves anything if the visit order and the id order disagree.

   WHY A SEPARATE RECORD RATHER THAN THE THEME'S. The rendered cards carry no
   product id — only variant ids and a handle in the link — so they cannot be
   matched back to `__sf_theme_recently`, which stores ids. Nothing in the theme
   writes that key either (an app or the live Parallax theme does), so its shape
   is not ours to rely on. This keeps its own list, keyed by the one thing the
   card does expose: the handle.

   Madrid still chooses WHICH products appear. This only orders them, and a card
   we have not seen before sorts to the end rather than disappearing — so the
   first load after this ships degrades to today's behaviour and heals itself.
   ------------------------------------------------------------------------- */

(function () {
  'use strict';

  var KEY = 'stl_recently_viewed_handles';
  var CAP = 20;

  function read() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, CAP)));
    } catch (e) {
      /* Private browsing, or storage full. Ordering is a nicety — never let it
         throw into the rest of the page. */
    }
  }

  /* `/products/<handle>` — also correct on `/collections/x/products/<handle>`,
     which is how a visitor arrives from a collection, and ignores any query
     string (the search fetch appends `?_pos=…&_sid=…`). */
  function handleFromPath(path) {
    var m = String(path).split('?')[0].match(/\/products\/([^/]+)\/?$/);
    return m ? m[1] : null;
  }

  function record() {
    var handle = handleFromPath(window.location.pathname);
    if (!handle) return;
    var list = read().filter(function (h) { return h !== handle; });
    list.unshift(handle);
    write(list);
  }

  function order(section) {
    var track = section.querySelector('.products-grid-section__wrapper');
    if (!track) return;

    var cards = Array.prototype.slice.call(
      track.querySelectorAll('.collection-product-card')
    );
    if (cards.length < 2) return;

    var history = read();

    var decorated = cards.map(function (card, i) {
      var link = card.querySelector('a[href*="/products/"]');
      var handle = link ? handleFromPath(link.getAttribute('href')) : null;
      var rank = handle ? history.indexOf(handle) : -1;
      return {
        card: card,
        /* Unknown handles keep their existing relative position, after everything
           we do know about. */
        rank: rank === -1 ? history.length + i : rank
      };
    });

    var already = decorated.every(function (d, i) {
      return i === 0 || decorated[i - 1].rank <= d.rank;
    });
    if (already) return;

    decorated
      .sort(function (a, b) { return a.rank - b.rank; })
      .forEach(function (d) { track.appendChild(d.card); });
  }

  /* -------------------------------------------------------------------------
     2. IMAGE SLIDERS

     Serena, 7 Sep 2026: the Recently Viewed cards do not swipe the way the PLP's
     do. They are the same markup — each card is a `.product-card-js` Swiper with
     6-8 slides — but no Swiper was ever constructed over them, so the only image
     behaviour left is the CSS hover swap.

     WHY. `product-card-slider.js` is already loaded by `sections/recently-viewed
     .liquid`, and its IIFE runs `initSlider()` once on load and again on
     `shopify:section:load` (theme editor only). Both fire while this section is
     still empty, because Madrid injects its cards from a fetch. Nothing re-runs
     it afterwards.

     It used to work by ACCIDENT. Madrid's `initProductSlider()` in `global.js`
     was `document`-wide and fired from each <product-recommendations> element's
     intersection callback, so You May Also Like reached across and initialised
     these cards on its way past. Scoping that to `this` was the D6 fix (4 Sep),
     which was right — it was also re-initialising the buy box's complementary
     slider — but it removed the side effect this section was relying on. Worth
     remembering: a bug fix can retire behaviour that nothing declared it owned.

     WHY THE THEME'S OWN FUNCTION RATHER THAN A NEW Swiper() HERE.
     `product-card-slider.js` exports its initialiser as `loadeMoreSlider_G` for
     exactly this purpose — `facets.js` calls it after a filter change. Reusing it
     means the cards get Madrid's own config (`cssMode: true`, the pagination and
     navigation wiring), which is the same config the PLP cards get, so the two
     surfaces cannot drift apart on a theme update. Re-declaring it here would
     have meant a second copy to keep in step.

     It is `document`-wide and carries its own `if (slider?.swiper) return` guard,
     so calling it repeatedly is safe and it never touches a slider somebody else
     has already built. That guard is also why this does NOT quietly take over You
     May Also Like: `global.js` injects and initialises those cards synchronously
     in one task, and a MutationObserver callback cannot run until that task ends,
     so they are always claimed before this sees them.

     Ordering runs first — moving an element after Swiper has measured it is asking
     for trouble, and `order()` reparents cards with `appendChild`. */
  function initCardSliders(section) {
    if (typeof loadeMoreSlider_G !== 'function') return;
    /* Test the `swiper` PROPERTY, the same thing the initialiser's own guard
       tests, rather than a `swiper-initialized` class — the class name is
       Swiper's to change, the property is what the theme already relies on. */
    var pending = Array.prototype.some.call(
      section.querySelectorAll('.product-card-js'),
      function (el) { return !el.swiper; }
    );
    if (!pending) return;
    try {
      loadeMoreSlider_G();
    } catch (e) {
      /* A slider failing to build must not take the ordering down with it. */
    }
  }

  function init() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[id$="__recently_viewed"]'),
      function (section) {
        order(section);
        initCardSliders(section);
        if (!window.MutationObserver) return;
        if (section.dataset.stlRvOrder) return;
        section.dataset.stlRvOrder = '1';
        /* Madrid injects the cards after fetching, so the first pass almost
           always runs against an empty section. Kept connected, not one-shot,
           for the same reason the carousel's is: the section can be re-rendered. */
        new MutationObserver(function () {
          order(section);
          initCardSliders(section);
        })
          .observe(section, { childList: true, subtree: true });
      }
    );
  }

  /* Record BEFORE ordering, so the product being viewed is already at the head of
     the list if it ever appears in its own section on a later page. */
  record();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
